const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'ltbb-ba-token';
const API_ROOT = `${BACKEND_URL}/api/v1`;

const staffHeaders = (json = false) => ({
  Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ''}`,
  ...(json ? { 'Content-Type': 'application/json' } : {}),
});

async function decode(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = data.detail;
    const message = typeof detail === 'string'
      ? detail
      : detail?.message || detail?.code || data.message || `Battle request failed: ${response.status}`;
    const error = new Error(message);
    Object.assign(error, typeof detail === 'object' ? detail : {}, data, { status: response.status });
    throw error;
  }
  return data;
}

const staffRequest = (path, options = {}) => fetch(`${API_ROOT}${path}`, options).then(decode);
const staffJson = (method, body) => ({ method, headers: staffHeaders(true), body: JSON.stringify(body) });

export const listBattles = (programId) => staffRequest(`/class-programs/${encodeURIComponent(programId)}/battles`, { headers: staffHeaders() });
export const createBattle = (programId, body) => staffRequest(`/class-programs/${encodeURIComponent(programId)}/battles`, staffJson('POST', body));
export const getBattle = (battleId) => staffRequest(`/battles/${encodeURIComponent(battleId)}`, { headers: staffHeaders() });
export const updateBattle = (battleId, body) => staffRequest(`/battles/${encodeURIComponent(battleId)}`, staffJson('PUT', body));
export const startBattle = (battleId) => staffRequest(`/battles/${encodeURIComponent(battleId)}/start`, { method: 'POST', headers: staffHeaders() });
export const renewJudgeLink = (battleId, judgeId) => staffRequest(`/battles/${encodeURIComponent(battleId)}/judges/${encodeURIComponent(judgeId)}/new-link`, { method: 'POST', headers: staffHeaders() });
export const setBattleCurrent = (battleId, entryId) => staffRequest(`/battles/${encodeURIComponent(battleId)}/current`, staffJson('POST', { entry_id: entryId }));
export const revealBattle = (battleId, force = false) => staffRequest(`/battles/${encodeURIComponent(battleId)}/reveal`, staffJson('POST', { force }));
export const chooseSuddenDeathWinner = (battleId, teamKey) => staffRequest(`/battles/${encodeURIComponent(battleId)}/sudden-death`, staffJson('POST', { team_key: teamKey }));
export const closeBattle = (battleId) => staffRequest(`/battles/${encodeURIComponent(battleId)}/close`, { method: 'POST', headers: staffHeaders() });
export const deleteBattle = (battleId) => staffRequest(`/battles/${encodeURIComponent(battleId)}`, { method: 'DELETE', headers: staffHeaders() });
export const getBattleScreen = (battleId) => staffRequest(`/battles/${encodeURIComponent(battleId)}/screen`, { headers: staffHeaders() });
export const useNewBattleEntryVersion = (battleId, entryId) => staffRequest(`/battles/${encodeURIComponent(battleId)}/entries/${encodeURIComponent(entryId)}/use-new-version`, { method: 'POST', headers: staffHeaders() });
export const resetBattleEntry = (battleId, entryId) => staffRequest(`/battles/${encodeURIComponent(battleId)}/entries/${encodeURIComponent(entryId)}/reset`, { method: 'POST', headers: staffHeaders() });

export async function exportBattleCsv(battleId, name = 'battle-results') {
  const response = await fetch(`${API_ROOT}/battles/${encodeURIComponent(battleId)}/export.csv`, { headers: staffHeaders() });
  if (!response.ok) return decode(response);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${name.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'battle-results'}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
  return true;
}

export const createJudgeSession = (secret) => fetch(`${API_ROOT}/battles/judge-session`, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ secret }),
}).then(decode);

const judgeWrite = (secret, path, body) => fetch(`${API_ROOT}${path}`, {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json', 'X-Battle-Judge': secret },
  body: JSON.stringify(body),
}).then(decode);

export const saveJudgeScore = (secret, entryId, body) => judgeWrite(secret, `/battles/judge/scores/${encodeURIComponent(entryId)}`, body);
export const saveJudgeWildcard = (secret, entryId) => judgeWrite(secret, '/battles/judge/wildcard', { entry_id: entryId });
